import { useState, useEffect, useRef } from 'react';
import BaseContainer from '@/components/baseContainer';
import { API_BASE_URL } from '@/config';
import ContactAgreementForm, { ContactAgreementFormData } from '@/components/form/contactAgreementForm';
import ContactBasicForm, { ContactBasicFormData } from '@/components/form/contactBasicForm';

type UserMini = {
  last_name_kanji: string;
  first_name_kanji: string;
  email: string;
};

const RegisterPage = () => {
  // ===== 子フォーム状態 =====
  const [basicFormData, setBasicFormData] = useState<{
    isValid: boolean;
    formData: ContactBasicFormData | undefined;
  }>({ isValid: false, formData: undefined });
  const [basicFormShouldSubmit, setBasicFormShouldSubmit] = useState(false);

  const [agreementFormData, setAgreementFormData] = useState<{
    isValid: boolean;
    formData: ContactAgreementFormData | undefined;
  }>({ isValid: false, formData: undefined });
  const [agreementFormShouldSubmit, setAgreementFormShouldSubmit] = useState(false);

  // ===== 同意 / ログイン / プリセット =====
  const [agreed, setAgreed] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [presetUser, setPresetUser] = useState<UserMini | null>(null);

  // ===== stg用 Basic ヘッダ生成 =====
  const buildBasicHeaderIfNeeded = () => {
    const envFlag = (process.env.REACT_APP_REQUIRE_BASIC === 'true');
    const isStgApi = /stg-2chiiki\.ana\.co\.jp/i.test(API_BASE_URL) || /\/stg[\/.]?/i.test(API_BASE_URL);
    const needBasic = envFlag || isStgApi;

    const user = '2chiiki-stg-wp';
    const pass = 'DNt~VqC*N.-5';
    if (!needBasic || !user || !pass) return undefined;

    const basic = btoa(`${user}:${pass}`);
    return `Basic ${basic}`;
  };

  // ===== /v1/user/info でログイン判定 & プリセット =====
  useEffect(() => {
    const token = localStorage.getItem('token') ?? '';
    const headers: HeadersInit = {};
    const basicHeader = buildBasicHeaderIfNeeded();

    if (basicHeader) {
      headers['Authorization'] = basicHeader;
      if (token.trim()) headers['X-Auth-Token'] = `Bearer ${token}`;
    } else if (token.trim()) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(`${API_BASE_URL}/v1/user/info`, { headers })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => {
        setIsLoggedIn(true);
        setPresetUser({
          last_name_kanji: data.last_name_kanji ?? '',
          first_name_kanji: data.first_name_kanji ?? '',
          email: data.email ?? '',
        });
      })
      .catch(() => {
        setIsLoggedIn(false);
        setPresetUser(null);
      });
  }, []);

  // ログイン時は同意スキップ扱い（送信値の整合も保つ）
  const skipAgreement = isLoggedIn;

  useEffect(() => {
    // if (skipAgreement) {
    //   setAgreementFormData({ isValid: true, formData: { privacy_checked: true } });
    //   setAgreementFormShouldSubmit(false);
    //   setAgreed(false); // 画面も即解放
    // }
  }, [skipAgreement]);

  // ===== 入力解放/ボタン活性判定 =====
  const canProceed =agreed ;

  const isFormValid = !!canProceed && basicFormData.isValid;

  // ===== 送信 =====
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // if (!isFormValid) {
    //   // 子にバリデーショントリガーだけ送る
    //   setBasicFormShouldSubmit(true);
    //   if (!skipAgreement) setAgreementFormShouldSubmit(true);
    //   setTimeout(() => {
    //     setBasicFormShouldSubmit(false);
    //     if (!skipAgreement) setAgreementFormShouldSubmit(false);
    //   }, 120);
    //   return;
    // }

    // 送信ボディ組み立て（ログイン時は氏名/メールをプリセット固定）
    const body: Record<string, unknown> = {
      ...(basicFormData.formData ?? {}),
      ...(agreementFormData.formData ?? {}),
      ...(isLoggedIn && presetUser
        ? {
            last_name_kanji: presetUser.last_name_kanji,
            first_name_kanji: presetUser.first_name_kanji,
            email: presetUser.email,
            privacy_checked: true,
          }
        : {}),
    };
    if (((agreementFormData.formData?.privacy_checked ?? skipAgreement) === true)) {
      (body as any).agree_privacy = true;
    }

    // ヘッダ
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token') ?? '';
    const basicHeader = buildBasicHeaderIfNeeded();

    if (basicHeader) {
      headers['Authorization'] = basicHeader;
      if (token.trim()) headers['X-Auth-Token'] = `Bearer ${token}`;
    } else if (token.trim()) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // POST
    fetch(`${API_BASE_URL}/v1/contact`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
      .then(res => (res.ok ? res.json() : res.json().then(e => { throw new Error(e.error || '登録に失敗しました'); })))
      .then(() => {
        alert('お問い合わせ完了しました。');
        const t = localStorage.getItem('token');
        window.location.href = t ? '/user/mypage' : '/user/login';
      })
      .catch(err => {
        alert(err.message);
      });
  };

  return (
    <div className="py-16">
      <form id="contactForm" ref={formRef} onSubmit={onSubmit}>
        <div className="flex flex-col gap-8 pt-8">
          <BaseContainer>
            <div className="xmd:w-[80rem] mx-auto">
              <p className="m-form-title mb-8">個人情報の取り扱いについて</p>
            </div>

            {/* プライバシーポリシー同意（ログイン時は表示しなくてもOKだが、ここでは常時表示＋スキップ処理で対応） */}
            <div className="xmd:w-[80rem] mx-auto flex flex-col gap-8">
              <p>
                個人情報の取扱いについて、下記の個人情報の取扱いに関する事項についてご確認いただき、同意される方は「同意する」をチェックし、フォームに入力の上、問い合わせ内容を送信してください。
              </p>
              <ContactAgreementForm
                enabled={true}
                onValidationChange={setAgreementFormData}
                shouldSubmit={agreementFormShouldSubmit}
                onAgreeChange={setAgreed} // ← チェックONの瞬間に true が上がる
              />
            </div>
          </BaseContainer>
        </div>

        <div className="flex flex-col gap-8 pt-8">
          <BaseContainer>
            <div className="xmd:w-[80rem] mx-auto flex flex-col gap-8">
              <ContactBasicForm
                enabled={canProceed}         // ← 同意（or ログイン）で有効化
                onValidationChange={setBasicFormData}
                shouldSubmit={basicFormShouldSubmit}
                preset={presetUser ?? undefined}
                locked={isLoggedIn}          // ← ログイン時は氏名/メールのみロック
              />
            </div>

            <div className="flex justify-center mt-12">
              <button
                type="submit"
                className={`m-btn fill ${!canProceed ? 'disabled' : ''}`}
                // disabled={!canProceed}
              >
                送信
              </button>
            </div>
          </BaseContainer>
        </div>
      </form>
    </div>
  );
};

export default RegisterPage;
